<template>
  <el-row :gutter="16" class="formulario-pdv-data-system">
    <el-col :span="12">
      <PDatePicker
        type="date"
        v-model="date"
        :disabled="selecionarPeriodo"
        :error="!selecionarPeriodo && !!errors?.date"
        placeholder="Selecione uma data"
        label="Data"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col :span="12" style="margin-top: 2px">
      <PSelect
        v-model="tipoDePesquisa"
        placeholder="Selecione um tipo de pesquisa"
        label="Tipo de pesquisa"
        :options="tiposDePesquisaOpt"
        filterable
        clearable
      />
    </el-col>
    <el-col :span="24" style="margin-top: 16px">
      <div class="help-block">
        <label>Tipos de consulta</label>
        <ul
          style="padding-left: 1.2em; list-style-type: decimal; margin: 12px 0"
        >
          <li><strong>PDV:</strong> Realiza a consulta de movimentos PDV.</li>
          <li>
            <strong>Liquidação de Contas a Receber:</strong> Realiza o download
            dos arquivos de liquidação dos títulos a receber.
          </li>
          <li>
            <strong>Contas a Pagar:</strong> Realiza o download dos arquivos de
            títulos a pagar.
          </li>
        </ul>
        <p style="margin-top: 0.5em">
          <em
            >Se nenhuma opção for selecionada, serão consultados todos os tipos
            automaticamente.</em
          >
        </p>
      </div>
    </el-col>
    <el-col :span="24" style="margin: 0.7rem 0">
      <PCheckbox label="Selecionar um período" v-model="selecionarPeriodo" />
    </el-col>
    <el-col :span="12" v-if="selecionarPeriodo" style="margin-bottom: 16px">
      <PDatePicker
        type="date"
        v-model="inicio"
        :error="selecionarPeriodo && !inicio"
        placeholder="Selecione uma data de início"
        label="Início"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col :span="12" v-if="selecionarPeriodo" style="margin-bottom: 16px">
      <PDatePicker
        type="date"
        v-model="fim"
        :error="selecionarPeriodo && !fim"
        placeholder="Selecione uma data de fim"
        label="Fim"
        style="width: 100%"
        clearable
      />
    </el-col>
  </el-row>
</template>

<script setup lang="ts">
import { PCheckbox, PDatePicker, PSelect } from "@f360-dev/f360-prisma";
import {
  CadastroDeIntegracaoDi,
  CadastroDeIntegracaoGateway
} from "../../../cadastro/services/CadastroDeIntegracaoGateway";
import { TWebservice } from "../../../../services/PainelDeIntegracaoesGateway";
import { TAPIRules } from "../../../../../../@types/IntegracoesAPIRules";
import { diContainer } from "@/inversify.config";
import { useForm } from "vee-validate";
import { computed, onMounted, ref } from "vue";
import {
  formPDVDataSystemSchema,
  IFormularioPDVDataSystemSchema
} from "./formularioPDVDataSystemSchema";

const props = defineProps<{
  webservice?: TWebservice;
  empresasOpt: { label: string; value: string }[];
  apiSelecionada?: TAPIRules;
}>();

const gateway = diContainer.get<CadastroDeIntegracaoGateway>(
  CadastroDeIntegracaoDi
);

const pdvDataSystem = ref<{
  consultartitulos: boolean;
  consultarcontasapagar: boolean;
}>({ consultartitulos: false, consultarcontasapagar: false });

const tiposDePesquisaOpt = computed(() => {
  const options = [
    {
      label: "PDV",
      value: "1"
    }
  ];
  if (pdvDataSystem.value.consultartitulos) {
    options.push({ label: "Liquidação de Contas a Receber", value: "2" });
  }

  if (pdvDataSystem.value.consultarcontasapagar) {
    options.push({ label: "Contas a Pagar", value: "3" });
  }

  return options;
});

const { handleSubmit, errors, setValues, values, defineField } =
  useForm<IFormularioPDVDataSystemSchema>({
    validationSchema: formPDVDataSystemSchema,
    initialValues: {
      date: new Date().toISOString().split("T")[0],
      selecionarPeriodo: false,
      inicio: null,
      fim: null,
      tipoDePesquisa: ""
    }
  });

const [date] = defineField("date");
const [selecionarPeriodo] = defineField("selecionarPeriodo");
const [inicio] = defineField("inicio");
const [fim] = defineField("fim");
const [tipoDePesquisa] = defineField("tipoDePesquisa");

const periodoUltrapassaLimiteDaApi = (): boolean => {
  const limite = props.apiSelecionada?.intervaloData;
  if (limite == null || limite <= 0) return false;

  const dataInicio = inicio.value != null ? new Date(inicio.value) : null;
  const dataFim = fim.value != null ? new Date(fim.value) : null;

  if (!dataInicio || !dataFim) return false;

  const diffEmDiasInicio = dataInicio.getDate();
  const diffEmDiasFim = dataFim.getDate();
  const diffEmDias = diffEmDiasFim - diffEmDiasInicio;

  if (diffEmDias < 0) return true;

  return diffEmDias >= limite;
};

const disabledSubmit = computed(() => {
  const missingDate = !date.value || String(date.value).trim() === "";
  const missingTipoDePesquisa =
    !tipoDePesquisa.value || String(tipoDePesquisa.value).trim() === "";

  if (missingDate || missingTipoDePesquisa) return true;

  if (selecionarPeriodo.value) {
    const missingInicio =
      inicio.value == null || String(inicio.value).trim() === "";
    const missingFim = fim.value == null || String(fim.value).trim() === "";

    return missingInicio || missingFim || periodoUltrapassaLimiteDaApi();
  }

  return false;
});

const obterPDVDataSystem = async () => {
  if (!gateway) return;

  const response = await gateway.getWebservice(props.webservice?.id ?? "");

  if (response?.Ok) {
    pdvDataSystem.value.consultartitulos = response.Result?.PDVDataSystem
      ?.consultartitulos
      ? true
      : false;
    pdvDataSystem.value.consultarcontasapagar = response.Result?.PDVDataSystem
      ?.consultarcontasapagar
      ? true
      : false;
  }
};

onMounted(async () => {
  await obterPDVDataSystem();

  setValues({
    date: new Date().toISOString().split("T")[0],
    selecionarPeriodo: false,
    inicio: null,
    fim: null,
    tipoDePesquisa: ""
  });
});

defineExpose({
  handleSubmit,
  errors,
  values,
  disabledSubmit
});
</script>

<style lang="scss" scoped>
.formulario-pdv-data-system {
  padding: 0 0 16px 0;

  .help-block {
    label {
      font-size: 14px;
      font-weight: 600;
      color: #333;
    }
    ul {
      list-style-type: decimal;

      li {
        font-size: 14px;
        font-weight: 400;
        color: #333;
        margin-bottom: 10px;
      }
    }
    p {
      font-size: 14px;
      font-weight: 400;
      color: #333;
    }
  }
}
</style>
